/**
 * 返还账务信息
 */
import React, { useMemo } from 'react';
import { Table } from 'antd';
import intl from 'react-intl-universal';

import { format2FullTimeStr } from '@/utils/date';
import getRandomId from '@/utils/uuid';
import { parseFen2Yuan } from '@/utils/number';
import ContentBlock from '@/components/Reservation/ContentBlock';
import { useDetailData, PostTypeEnum, BillInfo } from '@/pages/reservation/reverseRollback/helper';
import { ORDER_BILL_STATE_MAP } from '@/pages/bill/config';
import { PostTypeMap } from '@/pages/handover/components/DetailTab';

import styles from './index.module.scss';

// 负值账务类型 退款、冲调、优惠
const NegativeTypeList = [PostTypeEnum.Adjustment, PostTypeEnum.Discount, PostTypeEnum.Refund];
// 付款账务类型
const PaymentTypeList = [PostTypeEnum.Payment, PostTypeEnum.Refund];
// 消费账务类型
const ConsumeTypeList = [PostTypeEnum.Consumption, PostTypeEnum.Adjustment, PostTypeEnum.Discount];

const Index = () => {
  const { bills } = useDetailData();

  // 文本内容
  const renderTextContent = (text) => {
    return <div className={styles.textContent}>{text}</div>;
  };

  const columns = [
    { title: intl.get('drawer_extension_rollback_column_bill_order'), dataIndex: 'resvNo' },
    { title: intl.get('drawer_extension_rollback_column_bill_room'), dataIndex: 'roomNoText' },
    { title: intl.get('handover_detail_column_post'), dataIndex: 'postTypeText' },
    { title: intl.get('handover_detail_column_project'), dataIndex: 'subjectCodeName' },
    {
      title: intl.get('reports_ota_bill_col9_render'),
      dataIndex: 'detail',
      render: renderTextContent,
    },
    { title: intl.get('handover_detail_column_amount'), dataIndex: 'payAmountText' },
    { title: intl.get('handover_detail_column_consume'), dataIndex: 'consumeAmountText' },
    { title: intl.get('bill_invoice_modal_time'), dataIndex: 'createTimeText' },
    { title: intl.get('handover_detail_column_status'), dataIndex: 'statusText' },
    { title: intl.get('bill_remark'), dataIndex: 'remark', render: renderTextContent },
  ]; // 表格列

  // 行样式
  const getRowClassName = (record) => {
    return record.rowClassName;
  };

  const dataSource = useMemo(() => {
    const result = [];
    if (bills) {
      const formatBillItem = (item: BillInfo) => {
        const { roomNo, postType, amount, status, createTime } = item;
        const isNegativeType = NegativeTypeList.includes(postType);
        const amountText = parseFen2Yuan(isNegativeType ? -amount : amount);
        return {
          ...item,
          subs: null,
          roomNoText: roomNo || intl.get('app_reservation_room_queue_no'),
          payAmountText: PaymentTypeList.includes(postType) ? amountText : null,
          consumeAmountText: ConsumeTypeList.includes(postType) ? amountText : null,
          rowClassName: isNegativeType ? styles.negativeRow : null,
          postTypeText: PostTypeMap[postType],
          createTimeText: format2FullTimeStr(createTime),
          statusText: ORDER_BILL_STATE_MAP[status],
          indexId: getRandomId(),
        };
      };

      bills.forEach((topItem) => {
        result.push(formatBillItem(topItem));
        (topItem.subs || []).forEach((subItem) => {
          result.push(formatBillItem(subItem));
        });
      });
    }
    return result;
  }, [bills]);

  return (
    <ContentBlock
      title={intl.get('drawer_extension_rollback_block_bill')}
      className={styles.billInfoWrapper}
    >
      <Table
        className={styles.tableContent}
        scroll={{ x: 'max-content' }}
        rowKey="indexId"
        pagination={false}
        columns={columns}
        dataSource={dataSource}
        rowClassName={getRowClassName}
      />
    </ContentBlock>
  );
};

export default Index;
